package nl.astraeus.midi

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.midi.audio.VstChipWorklet
import nl.astraeus.midi.view.Keyboard
import nl.astraeus.midi.view.PracticeStaff
import nl.astraeus.midi.view.components.Dropdown
import nl.astraeus.midi.view.components.Selectable
import nl.astraeus.midi.view.renderCss
import org.w3c.dom.events.Event

// language=css
val mainCss = """
  html, body {
      font-family: Bravura, arial,sans-serif;
      font-size: 16px;
      margin: 0;
      padding: 0;
      user-select: none;
  }
  .title-bar {
    width: 90%;
    min-width: 1200px;
    height: 64px;
    padding: 0 5%;
    border-bottom: 1px solid #eee;
    
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background-color: #f8f8f8;
    margin-bottom: 1rem;
    
    .mkt-version {
      position: relative;
      top: 17px; 
      font-size: x-large;
      text-transform: capitalize;
    }
  
    .midi-vst {
      padding: 0 2rem;
      color: black;
      
      :visited {
        color: black;
      }
    }
  }
  .midi-select {
    .midi-label {
      margin-right: 10px;
      font-size: larger;
      position: relative;
      top: 3px;
    }
    .no-midi {
      margin-right: 10px;
      font-size: larger;
      position: relative;
      top: 5px;
    }
    .no-midi-retry {
      font-size: larger;
      position: relative;
      top: 3px;
      padding: 5px;
      border-radius: 5px;
      background-color: #5899cc;
      color: white;
    }
    .midi-selection {
      margin-right: 1rem;
    }
  }
  .enable-audio {
    font-size: larger;
    label {
      position: relative;
      top: 3px;
    }
    input {
      position: relative;
      width: 35px;
      height: 35px;
      top: 13px;
    }
  }
  .canvas-content {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
  }
  .clef-select {
    transform-origin: 0 0; 
    position: absolute;
    z-index: 10;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 1rem;
    gap: 1rem;
    
    .clef-button {
      font-size: 4rem;
      cursor: pointer;
      border: 1px solid #ccc;
      border-radius: 8px;
      padding: 0.5rem;
      width: 80px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: white;
      user-select: none;
      
      &.selected {
        background-color: #e0e0e0;
        border: 2px solid #5899cc;
      }
      
      &:hover {
        background-color: #f0f0f0;
      }
    }
  }
  .staff-content {
    flex-grow: 1;
  }
""".trimIndent()

class MainKomponent : Komponent() {
  var showTreble = true
  val channels = Array(17) {
    if (it == 0) {
      Selectable("-1", "Any")
    } else {
      Selectable((it - 1).toString(), "$it")
    }
  }.toList()

  var channel = window.localStorage.getItem(Midi.SELECTED_MIDI_CHANNEL_KEY)?.toInt() ?: -1

  //val staffs = CanvasStaffs()
  var practice = PracticeStaff(channel)
  var keyboard = Keyboard(channel) { midi ->
    practice.handleMidi(midi)
  }

  var selectedInput: Selectable? = null
  var audioEnabled: Boolean = false
  var audioStarted: Boolean = false

  init {
    val savedId = window.localStorage.getItem(Midi.SELECTED_MIDI_INPUT_KEY)
    if (savedId != null) {
      val found = Midi.inputs.find { it.id == savedId }
      if (found != null) {
        selectedInput = Selectable(found.id, found.name)
        Midi.setInput(found) { midi ->
          practice.handleMidi(midi)
          keyboard.handleMidi(midi)
        }
      }
    }

    window.onresize = ::scaleHeader
  }

  override fun HtmlBuilder.render() {
    div("container") {
      div("title-bar") {
        id = "title-bar"
        div("mkt-version") {
          +(document.getElementById("title")?.textContent ?: "MKT")
        }
        div("midi-select") {
          if (Midi.inputs.isEmpty()) {
            span("no-midi") {
              +"No MIDI inputs available"

            }
            span("no-midi-retry") {
              +"Retry"
              onClickFunction = {
                Midi.start {
                  requestUpdate()
                }
              }
            }
          } else {
            span("midi-label") {
              +"MIDI in:"
            }
            span("midi-selection") {
              include(
                Dropdown(
                  selectedInput,
                  Midi.inputs.map { Selectable(it.id, it.name) },
                  onSelect = {
                    console.log("Selected", it)
                    window.localStorage.setItem(Midi.SELECTED_MIDI_INPUT_KEY, it.value)
                    Midi.setInput(it.value) { midi ->
                      //staffs.handleMidi(midi)
                      practice.handleMidi(midi)
                      keyboard.handleMidi(midi)
                    }
                  },
                  onHover = { console.log("Hover", it) },
                )
              )
            }
            span("midi-label") {
              +"MIDI channel:"
            }
            span("midi-selection") {
              include(
                Dropdown(
                  channels[channel + 1],
                  channels,
                  true,
                  onSelect = {
                    console.log("Selected", it)
                    window.localStorage.setItem(Midi.SELECTED_MIDI_CHANNEL_KEY, it.value)
                    channel = it.value.toInt()
                    practice.channel = it.value.toInt()
                    keyboard.channel = it.value.toInt()
                  },
                  onHover = { console.log("Hover", it) },
                )
              )
            }
          }
          span("enable-audio") {
            label {
              +"Audio:"
            }
            input {
              type = InputType.checkBox
              checked = audioEnabled

              onClickFunction = {
                audioEnabled = !audioEnabled
                if (!audioEnabled) {
                  VstChipWorklet.volume = 0.0
                } else if (!audioStarted) {
                  VstChipWorklet.create {
                    audioStarted = true

                    VstChipWorklet.volume = 0.9
                    VstChipWorklet.waveform = 2
                    VstChipWorklet.dutyCycle = 0.75
                    VstChipWorklet.fmModFreq = 1.0
                    VstChipWorklet.fmModAmp = 0.0
                    VstChipWorklet.amModFreq = 0.0
                    VstChipWorklet.amModAmp = 0.0
                    VstChipWorklet.feedback = 0.0
                    VstChipWorklet.delay = 0.0
                    VstChipWorklet.delayDepth = 0.0
                    VstChipWorklet.attack = 0.010
                    VstChipWorklet.decay = 0.025
                    VstChipWorklet.sustain = 0.2
                    VstChipWorklet.release = 0.2
                  }
                } else {
                  VstChipWorklet.volume = 0.9
                }
                requestUpdate()
              }
            }
          }
        }
        div("midi-vst") {
          a {
            href = "https://www.midi-vst.com"
            h2 {
              +"MIDI Vst"
            }
          }
        }
      }
      div("clef-select") {
        id = "clef-select"
        div("clef-button${if (showTreble) " selected" else ""}") {
          +"\uE050"
          onClickFunction = {
            showTreble = true
            practice = PracticeStaff(channel, showTreble)
            keyboard = Keyboard(channel) { midi ->
              practice.handleMidi(midi)
            }
            requestUpdate()
          }
        }
        div("clef-button${if (!showTreble) " selected" else ""}") {
          +"\uE062"
          onClickFunction = {
            showTreble = false
            practice = PracticeStaff(channel, showTreble)
            keyboard = Keyboard(channel, Note.C2) { midi ->
              practice.handleMidi(midi)
            }
            requestUpdate()
          }
        }
      }
      div("canvas-content") {
        div("staff-content") {
          include(practice)
        }
        div {
          include(keyboard)
        }
      }
    }
    scaleHeader()
  }

  private fun scaleHeader(e: Event? = null) {
    console.log("Resizing", e)
    val container = document.getElementById("title-bar")
    val clefs = document.getElementById("clef-select")

    val vpWidth = window.innerWidth
    val vpHeight = window.innerHeight

    val containerWidth = 2000.0
    val containerHeight = 60

    val scaleX: Double = vpWidth / containerWidth
    val scaleY: Double = 1.0 //vpHeight / containerHeight.toDouble()

    val left = (vpWidth - containerWidth * scaleX) / 2

    /*    container?.setAttribute(
          "style",
          "position: relative; transform-origin: 0 0; transform: scale($scaleX);"
        )*/
    clefs?.setAttribute(
      "style",
      "transform: scale($scaleX);"
    )
  }
}

fun main() {
  renderCss()

  Midi.start {
    console.log("MIDI start!")
    document.getElementById("loading")?.also {
      document.body?.removeChild(it)
    }

    document.asDynamic().fonts.load("120px Bravura").then {
      console.log("Bravura font loaded!")

      Komponent.create(document.body!!, MainKomponent())
    }
  }

  window.addEventListener("load", { event ->
    window.navigator.serviceWorker.register("/service-worker.js").then({ registration ->
      console.log("ServiceWorker registration successful with scope: ", registration.scope)
    }, { err ->
      console.log("ServiceWorker registration failed: ", err)
    })
  }, {})

}
